/**
 * React Query-хуки поверх API.
 *
 * Ключи кэша собраны в одном месте: инвалидация после ответа игрока или
 * публикации квеста должна попадать ровно в те запросы, которые устарели.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import i18next from "i18next";

import { fitWithin } from "../lib/image";

import { ApiError, request, uploadToStorage } from "./client";
import { useTranslation } from "react-i18next";

import type {
  AuthSettings,
  Feedback,
  QuestDraft,
  FeedbackKind,
  Room,
  CreateQuestRound,
  ResendVerificationResponse,
  LeaderboardResponse,
  ListQuestsResponse,
  ListRoomInvitesResponse,
  ListLandingPhotosResponse,
  ListAnnouncementsResponse,
  PasswordResetRequested,
  PlayerProfile,
  SharedResult,
  Tag,
  Quest,
  Session,
  SessionResults,
  SubmitGuessResponse,
  UploadUrl,
  User,
  VerifyEmailResponse,
} from "./types";

export const queryKeys = {
  authSettings: () => ["auth", "settings"] as const,
  myFeedback: () => ["feedback", "me"] as const,
  myRoom: () => ["room", "me"] as const,
  quests: (params?: unknown) => ["quests", params] as const,
  quest: (id: string) => ["quest", id] as const,
  questDraft: (id: string) => ["quest", id, "draft"] as const,
  dailyQuest: (date?: string) => ["quest", "daily", date ?? "today"] as const,
  myQuests: () => ["quests", "mine"] as const,
  session: (id: string) => ["session", id] as const,
  sessionResults: (id: string) => ["session", id, "results"] as const,
  dailyLeaderboard: (date?: string) => ["leaderboard", "daily", date ?? "today"] as const,
  questLeaderboard: (questId: string) => ["leaderboard", "quest", questId] as const,
  profile: (userId: string) => ["profile", userId] as const,
  tags: () => ["tags"] as const,
  favoriteQuests: () => ["quests", "favorites"] as const,
  roomInvites: () => ["rooms", "invites"] as const,
  landingPhotos: () => ["landing", "photos"] as const,
  // Язык — часть ключа: текст анонса приходит с сервера уже переведённым,
  // а окно всплывает в первую же секунду, когда язык пользователя ещё
  // подгружается. Общий сброс кэша по languageChanged сюда не успевает:
  // запрос к тому моменту уже в полёте, и его повтор схлопывается.
  announcements: (unseenOnly: boolean, language: string) =>
    ["announcements", unseenOnly, language] as const,
  sharedResult: (sessionId: string, playerId?: string) =>
    ["shared", sessionId, playerId ?? ""] as const,
};

export type QuestSort = "QUEST_SORT_CREATED" | "QUEST_SORT_VIEWS" | "QUEST_SORT_RATING";

interface QuestListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  tags?: string[];
  sort?: QuestSort;
  ascending?: boolean;
}

export function useQuests(params: QuestListParams = {}) {
  const { page = 1, pageSize = 12, search, tags, sort, ascending } = params;

  return useQuery({
    queryKey: queryKeys.quests({ page, pageSize, search, tags, sort, ascending }),
    queryFn: () =>
      request<ListQuestsResponse>("/api/v1/quests", {
        query: {
          "page.page": page,
          "page.pageSize": pageSize,
          search,
          tags,
          sort,
          ascending: ascending ? true : undefined,
        },
      }),
    // Каталог меняется редко: держим данные свежими минуту, чтобы переходы
    // между страницами не дёргали сеть.
    staleTime: 60_000,
  });
}

/**
 * Оценка квеста и избранное.
 *
 * Обе мутации возвращают уже пересчитанный квест, поэтому его карточка
 * обновляется без похода в сеть, а каталог и избранное просто помечаются
 * устаревшими: там квест может стоять на другой странице или в другом порядке.
 */
export function useRateQuest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { questId: string; rating: number }) =>
      request<Quest>(`/api/v1/quests/${input.questId}/rating`, {
        method: "PUT",
        body: { rating: input.rating },
      }),
    onSuccess: (quest) => {
      queryClient.setQueryData(queryKeys.quest(quest.id ?? ""), quest);
      void queryClient.invalidateQueries({ queryKey: ["quests"] });
    },
  });
}

export function useSetQuestFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { questId: string; favorite: boolean }) =>
      request<Quest>(`/api/v1/quests/${input.questId}/favorite`, {
        method: "PUT",
        body: { favorite: input.favorite },
      }),
    onSuccess: (quest) => {
      queryClient.setQueryData(queryKeys.quest(quest.id ?? ""), quest);
      void queryClient.invalidateQueries({ queryKey: ["quests"] });
    },
  });
}

export function useFavoriteQuests(enabled = true) {
  return useQuery({
    queryKey: queryKeys.favoriteQuests(),
    queryFn: () => request<ListQuestsResponse>("/api/v1/quests/favorites"),
    enabled,
  });
}

/**
 * Кадры первого экрана. Их выбирает редакция, а не каталог: иначе первое,
 * что видит незнакомый человек, зависит от того, чей квест последним прошёл
 * модерацию.
 */
export function useLandingPhotos() {
  return useQuery({
    queryKey: queryKeys.landingPhotos(),
    queryFn: () => request<ListLandingPhotosResponse>("/api/v1/landing-photos"),
    // Меняются они редко, а первый экран открывают часто.
    staleTime: 10 * 60_000,
  });
}

export function useQuest(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.quest(id ?? ""),
    queryFn: () => request<Quest>(`/api/v1/quests/${id}`),
    enabled: Boolean(id),
  });
}

export function useDailyQuest(options?: Partial<UseQueryOptions<Quest>>) {
  return useQuery<Quest>({
    queryKey: queryKeys.dailyQuest(),
    queryFn: () => request<Quest>("/api/v1/quests/daily"),
    // Квест дня один на сутки, перезапрашивать его при каждом фокусе незачем.
    staleTime: 5 * 60_000,
    retry: false,
    ...options,
  });
}

export function useMyQuests(enabled = true) {
  return useQuery({
    queryKey: queryKeys.myQuests(),
    queryFn: () => request<ListQuestsResponse>("/api/v1/quests/mine"),
    enabled,
  });
}

export function useSession(id: string | undefined, refetchInterval?: number) {
  return useQuery({
    queryKey: queryKeys.session(id ?? ""),
    queryFn: () => request<Session>(`/api/v1/sessions/${id}`),
    enabled: Boolean(id),
    refetchInterval,
  });
}

export function useSessionResults(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sessionResults(id ?? ""),
    queryFn: () => request<SessionResults>(`/api/v1/sessions/${id}/results`),
    enabled: Boolean(id),
  });
}

export function useDailyLeaderboard(date?: string) {
  return useQuery({
    queryKey: queryKeys.dailyLeaderboard(date),
    queryFn: () =>
      request<LeaderboardResponse>("/api/v1/leaderboards/daily", {
        query: { date, limit: 50 },
      }),
  });
}

/** Лидерборд квеста. Только для вошедших: гостю сервер его не отдаёт. */
export function useQuestLeaderboard(questId: string | undefined, authenticated: boolean) {
  return useQuery({
    queryKey: queryKeys.questLeaderboard(questId ?? ""),
    queryFn: () =>
      request<LeaderboardResponse>(`/api/v1/leaderboards/quests/${questId}`, {
        query: { limit: 20 },
      }),
    enabled: Boolean(questId) && authenticated,
  });
}

export function usePlayerProfile(userId = "me") {
  return useQuery({
    queryKey: queryKeys.profile(userId),
    queryFn: () => request<PlayerProfile>(`/api/v1/players/${userId}`),
  });
}

export function useCreateSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      questId: string;
      mode: "GAME_MODE_SOLO" | "GAME_MODE_MULTIPLAYER";
      roundDurationSeconds?: number;
      roundCount?: number;
    }) => request<Session>("/api/v1/sessions", { method: "POST", body: input }),
    // Ответ кладётся в кэш сразу: экран раунда иначе открывается пустым и
    // заново идёт за той же сессией, а отсчёт перед стартом успевает
    // наполовину истечь, пока она едет.
    onSuccess: (session) => queryClient.setQueryData(queryKeys.session(session.id ?? ""), session),
  });
}

export function useJoinSession() {
  return useMutation({
    mutationFn: (joinCode: string) =>
      request<Session>("/api/v1/sessions/join", {
        method: "POST",
        body: { joinCode },
      }),
  });
}

/**
 * Выход из комнаты.
 *
 * До старта участник исчезает из лобби совсем, после — перестаёт задерживать
 * раунд, но его результаты остаются. Кэш сессии инвалидируется, чтобы список
 * участников обновился у самого ушедшего.
 */
export function useLeaveSession(sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      request<void>(`/api/v1/sessions/${sessionId}/leave`, { method: "POST", body: {} }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.session(sessionId) });
    },
  });
}

/** Отметка о том, что приветственная инструкция прочитана. */
export function useMarkGuideSeen() {
  return useMutation({
    mutationFn: () => request<User>("/api/v1/auth/me/guide-seen", { method: "POST", body: {} }),
  });
}

/**
 * Квест глазами автора: с ответами раундов, которых играющий не видит.
 * Нужен форме правки — без ответов править было бы нечего.
 */
export function useQuestDraft(questId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.questDraft(questId ?? ""),
    queryFn: () => request<QuestDraft>(`/api/v1/quests/${questId}/draft`),
    enabled: Boolean(questId),
    // Форма забирает данные один раз при открытии: перезапрос затирал бы
    // то, что автор уже успел поправить.
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Правка своего квеста.
 *
 * Черновик правится на месте, у опубликованного правка уходит новой версией —
 * прежняя остаётся играбельной. Что именно произошло, видно по идентификатору
 * в ответе.
 */
export function useUpdateQuest(questId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: {
      title: string;
      description?: string;
      tags?: string[];
      coverObjectKey?: string;
      rounds: CreateQuestRound[];
    }) => request<Quest>(`/api/v1/quests/${questId}`, { method: "PUT", body }),
    onSuccess: () => {
      // Ключ каталога и «моих квестов» начинается одинаково, поэтому одной
      // инвалидации хватает на оба списка.
      void queryClient.invalidateQueries({ queryKey: ["quests"] });
    },
  });
}

/* --- Обратная связь --- */

/**
 * Отправка сообщения о проблеме.
 *
 * Контекст (кадр, квест) прикладывается там, где он известен: без него
 * модератор не найдёт, о чём речь, а игрок не обязан объяснять словами,
 * какой именно из пяти кадров был кривым.
 */
export function useSubmitFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      kind: FeedbackKind;
      topic?: string;
      message: string;
      photoId?: string;
      questId?: string;
    }) => request<Feedback>("/api/v1/feedback", { method: "POST", body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.myFeedback() });
    },
  });
}

/** Собственные сообщения игрока вместе с ответами модератора. */
export function useMyFeedback(enabled = true) {
  return useQuery({
    queryKey: queryKeys.myFeedback(),
    queryFn: () =>
      request<{ items?: Feedback[] }>("/api/v1/feedback/me?page.pageSize=50"),
    enabled,
    staleTime: 30_000,
  });
}

/* --- Комнаты ---
 *
 * Комната живёт дольше партии: собрались один раз, а квестов сыграли сколько
 * захотели. Поэтому она не привязана к квесту и её состояние нужно на любой
 * странице, а не только в лобби.
 */

/** Комната, в которой состоит игрок. Её отсутствие — не ошибка. */
export function useMyRoom(enabled = true) {
  return useQuery<Room | null>({
    queryKey: queryKeys.myRoom(),
    queryFn: async () => {
      try {
        return await request<Room>("/api/v1/rooms/me");
      } catch (error) {
        // 404 значит «комнаты нет» — обычное состояние, а не сбой.
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    enabled,
    retry: false,
    staleTime: 10_000,
  });
}

export function useCreateRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => request<Room>("/api/v1/rooms", { method: "POST", body: {} }),
    onSuccess: (room) => queryClient.setQueryData(queryKeys.myRoom(), room),
  });
}

export function useJoinRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (joinCode: string) =>
      request<Room>("/api/v1/rooms/join", { method: "POST", body: { joinCode } }),
    onSuccess: (room) => queryClient.setQueryData(queryKeys.myRoom(), room),
  });
}

/**
 * Приглашения в комнату.
 *
 * Опрашиваются, а не приходят по сокету: комната знает только своих, и до
 * приглашаемого её канал не достаёт, а поднимать канал на каждого
 * пользователя ради одного сообщения в час не окупается.
 */
export function useRoomInvites(enabled = true) {
  return useQuery({
    queryKey: queryKeys.roomInvites(),
    queryFn: () => request<ListRoomInvitesResponse>("/api/v1/rooms/invites"),
    enabled,
    refetchInterval: 15_000,
    // Приглашение зовёт играть сейчас: вернувшись к вкладке, человек должен
    // увидеть его сразу, а не через четверть минуты.
    refetchOnWindowFocus: true,
  });
}

export function useInvitePlayer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) =>
      request<Room>("/api/v1/rooms/invites", { method: "POST", body: { userId } }),
    onSuccess: (room) => queryClient.setQueryData(queryKeys.myRoom(), room),
  });
}

export function useRespondToInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { inviteId: string; accept: boolean }) =>
      request<{ room?: Room }>(`/api/v1/rooms/invites/${input.inviteId}/respond`, {
        method: "POST",
        body: { accept: input.accept },
      }),
    onSuccess: (response) => {
      if (response.room) queryClient.setQueryData(queryKeys.myRoom(), response.room);
      void queryClient.invalidateQueries({ queryKey: queryKeys.roomInvites() });
    },
  });
}

/**
 * Анонсы проекта. Непоказанные всплывают окном, все вместе — списком
 * в профиле.
 */
export function useAnnouncements(unseenOnly: boolean, enabled = true) {
  const { i18n } = useTranslation();

  return useQuery({
    queryKey: queryKeys.announcements(unseenOnly, i18n.resolvedLanguage ?? "en"),
    queryFn: () =>
      request<ListAnnouncementsResponse>("/api/v1/announcements", {
        query: { unseenOnly: unseenOnly ? true : undefined },
      }),
    enabled,
  });
}

export function useMarkAnnouncementSeen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      request<void>(`/api/v1/announcements/${id}/seen`, { method: "POST", body: {} }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["announcements"] }),
  });
}

export function useLeaveRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roomId: string) =>
      request<void>(`/api/v1/rooms/${roomId}/leave`, { method: "POST", body: {} }),
    onSuccess: () => queryClient.setQueryData(queryKeys.myRoom(), null),
  });
}

/**
 * Исключение участника хозяином.
 *
 * Нужно для тех, кто закрыл вкладку и остался висеть в составе: комната для
 * того и показывает присутствие, чтобы хозяин видел, кого уже нет.
 */
export function useRemoveRoomMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { roomId: string; userId: string }) =>
      request<void>(`/api/v1/rooms/${input.roomId}/members/${input.userId}/remove`, {
        method: "POST",
        body: {},
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.myRoom() });
    },
  });
}

/** Запуск партии комнатой: в игру уходят все, кто сейчас на связи. */
export function useStartRoomGame() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      roomId: string;
      questId: string;
      roundDurationSeconds?: number;
      roundCount?: number;
    }) =>
      request<Session>(`/api/v1/rooms/${input.roomId}/games`, {
        method: "POST",
        // Ноль — осмысленное значение (игра без ограничения по времени),
        // поэтому поля отбрасываются по undefined, а не по ложности.
        body: {
          questId: input.questId,
          ...(input.roundDurationSeconds === undefined
            ? {}
            : { roundDurationSeconds: input.roundDurationSeconds }),
          ...(input.roundCount === undefined ? {} : { roundCount: input.roundCount }),
        },
      }),
    onSuccess: (session) => {
      queryClient.setQueryData(queryKeys.session(session.id ?? ""), session);
      void queryClient.invalidateQueries({ queryKey: queryKeys.myRoom() });
    },
  });
}

export function useStartSession(sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      request<Session>(`/api/v1/sessions/${sessionId}/start`, {
        method: "POST",
        body: {},
      }),
    onSuccess: (session) => {
      queryClient.setQueryData(queryKeys.session(sessionId), session);
    },
  });
}

export function useSubmitGuess(sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      photoId: string;
      latitude: number;
      longitude: number;
      guessedYear: number;
    }) =>
      request<SubmitGuessResponse>(`/api/v1/sessions/${sessionId}/guesses`, {
        method: "POST",
        body: {
          photoId: input.photoId,
          guess: { latitude: input.latitude, longitude: input.longitude },
          guessedYear: input.guessedYear,
        },
      }),
    onSuccess: () => {
      // Сессия здесь намеренно не инвалидируется: её перезагрузка сменила бы
      // номер текущего раунда и смахнула экран с разбором ответа. Игрок
      // переходит к следующему раунду сам, тогда данные и обновляются.
      void queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
      void queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}

/**
 * Публичные настройки форм входа и регистрации: ключ капчи, порог сложности
 * пароля и версия согласия.
 *
 * Значения приходят с сервера, а не задаются во фронтенде отдельно: иначе их
 * пришлось бы держать синхронными вручную, и форма обещала бы одно, а сервер
 * проверял другое.
 */
export function useAuthSettings() {
  return useQuery({
    queryKey: queryKeys.authSettings(),
    queryFn: () => request<AuthSettings>("/api/v1/auth/settings"),
    // Настройки меняются только при переразвёртывании сервера.
    staleTime: 10 * 60_000,
    retry: 1,
  });
}

/**
 * Подтверждение адреса по ссылке из письма.
 *
 * Метод публичный: по ссылке переходят в том числе из другого браузера,
 * где сессии ещё нет.
 */
export function useVerifyEmail() {
  return useMutation({
    mutationFn: (token: string) =>
      request<VerifyEmailResponse>("/api/v1/auth/verify-email", {
        method: "POST",
        body: { token },
      }),
  });
}

/** Повторная отправка письма с подтверждением самому себе. */
export function useResendVerification() {
  return useMutation({
    mutationFn: () =>
      request<ResendVerificationResponse>("/api/v1/auth/verify-email/resend", {
        method: "POST",
        body: {},
      }),
  });
}

/**
 * Сохраняет выбранный язык в профиле.
 *
 * В браузере он и так запомнен, но серверу язык нужен для писем: иначе
 * подтверждение придёт на языке того устройства, с которого человек когда-то
 * регистрировался.
 */
export function useUpdateLanguage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (language: string) =>
      request<User>("/api/v1/auth/me", { method: "PATCH", body: { language } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}

/**
 * Результат по ссылке «поделиться».
 *
 * Метод публичный: ссылку открывают в том числе те, у кого аккаунта нет.
 * Ответов раундов он не отдаёт — иначе ссылка портила бы квест получателю.
 */
export function useSharedResult(sessionId: string, playerId?: string) {
  return useQuery({
    queryKey: queryKeys.sharedResult(sessionId, playerId),
    queryFn: () =>
      request<SharedResult>(`/api/v1/sessions/${sessionId}/shared`, {
        query: playerId ? { userId: playerId } : undefined,
      }),
    enabled: Boolean(sessionId),
  });
}

/**
 * Справочник тегов.
 *
 * Теги хранятся кодами, а показываются на языке интерфейса: свободный текст
 * нельзя перевести, и англоязычный игрок видел бы «города».
 */
export function useTags() {
  return useQuery({
    queryKey: queryKeys.tags(),
    queryFn: () => request<{ tags?: Tag[] }>("/api/v1/tags"),
    // Справочник ведёт модератор и меняется он редко.
    staleTime: 10 * 60_000,
  });
}

/** Правка собственного профиля: имя, язык и аватар. */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: {
      username?: string;
      language?: string;
      avatarObjectKey?: string;
      updateAvatar?: boolean;
    }) => request<User>("/api/v1/auth/me", { method: "PATCH", body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}

/**
 * Запрос смены адреса почты. Письмо уходит на новый адрес, и адрес меняется
 * только после перехода по ссылке из него.
 */
export function useRequestEmailChange() {
  return useMutation({
    mutationFn: (body: { newEmail: string; password: string }) =>
      request<{ pendingEmail?: string }>("/api/v1/auth/email/change", {
        method: "POST",
        body,
      }),
  });
}

/** Смена пароля вошедшим пользователем. */
export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      request<Record<string, never>>("/api/v1/auth/password/change", {
        method: "POST",
        body,
      }),
  });
}

/** Подтверждение нового адреса по ссылке из письма. */
export function useConfirmEmailChange() {
  return useMutation({
    mutationFn: (token: string) =>
      request<User>("/api/v1/auth/email/confirm", { method: "POST", body: { token } }),
  });
}

/**
 * Загрузка аватара: та же схема, что у фотографий раундов — presigned-ссылка,
 * файл уходит прямо в хранилище, backend получает только ключ.
 */
export function useUploadAvatar() {
  return useMutation({
    mutationFn: async (source: File): Promise<string> => {
      // Аватар показывается размером с монету: хранить под него мегабайты
      // незачем.
      const file = await fitWithin(source, 512, 512);

      const presigned = await request<UploadUrl>("/api/v1/media/uploads", {
        method: "POST",
        body: { contentType: file.type, sizeBytes: file.size },
      });

      if (!presigned.uploadUrl || !presigned.objectKey) {
        throw new Error(i18next.t("error.upload"));
      }

      await uploadToStorage(presigned.uploadUrl, file);
      return presigned.objectKey;
    },
  });
}

/**
 * Запрос ссылки для смены забытого пароля.
 *
 * Сервер отвечает одинаково и на известный, и на неизвестный адрес, поэтому
 * интерфейс тоже не показывает разницы: иначе форма превратилась бы в способ
 * проверять, кто зарегистрирован в сервисе.
 */
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (body: { email: string; captchaToken?: string }) =>
      request<PasswordResetRequested>("/api/v1/auth/password/forgot", {
        method: "POST",
        body,
      }),
  });
}

/** Смена пароля по ссылке из письма: метод публичный, вход не нужен. */
export function useResetPassword() {
  return useMutation({
    mutationFn: (body: { token: string; newPassword: string }) =>
      request<Record<string, never>>("/api/v1/auth/password/reset", {
        method: "POST",
        body,
      }),
  });
}

/**
 * Загрузка фото для пользовательского квеста: сначала presigned-ссылка,
 * затем файл уходит прямо в хранилище. Backend байты не видит.
 */
export function useUploadPhoto() {
  return useMutation({
    mutationFn: async (source: File): Promise<string> => {
      // Снимок с телефона легко весит 10 МБ при 8000 × 6000 пикселей. На
      // экране кадр всё равно не больше окна, а грузиться будет секунды.
      const file = await fitWithin(source);

      const presigned = await request<UploadUrl>("/api/v1/media/uploads", {
        method: "POST",
        body: { contentType: file.type, sizeBytes: file.size },
      });

      if (!presigned.uploadUrl || !presigned.objectKey) {
        throw new Error(i18next.t("error.upload"));
      }

      await uploadToStorage(presigned.uploadUrl, file);
      return presigned.objectKey;
    },
  });
}

export function useCreateQuest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      title: string;
      description: string;
      tags: string[];
      rounds: CreateQuestRound[];
    }) =>
      request<Quest>("/api/v1/quests", {
        method: "POST",
        body: { ...input, rightsConfirmed: true },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.myQuests() });
    },
  });
}
